import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import * as adminHandlers from '../../../../routes/Admin/redux/handlers';
import SubBrandCard from '../SubBrandCard';
import SubBrandModal from '../SubBrandModal';
import StatusToggle from '../../../Shared/StatusToggle';
import ImageUploadButton from '../../../Shared/ImageUploadButton';

import { DrawerIcon } from '../../../../assets/icons/DrawerIcon';
import { BrandInfo } from '../../../../assets/icons/BrandInfo';
import { AdditionalInfoIcon } from '../../../../assets/icons/AdditionalInfoIcon';
import { EditIcon } from '../../../../assets/icons/EditIcon';
import { SaveIcon } from '../../../../assets/icons/SaveIcon';


// Validation Schema
const brandValidationSchema = Yup.object().shape({
    name: Yup.string()
        .required('Brand name is required')
        .min(2, 'Brand name must be at least 2 characters')
        .max(100, 'Brand name must be less than 100 characters'),
    primary_sequence: Yup.number()
        .required('Primary sequence is required')
        .positive('Primary sequence must be a positive number')
        .integer('Primary sequence must be an integer'),
    secondary_sequence: Yup.number()
        .required('Secondary sequence is required')
        .positive('Secondary sequence must be a positive number')
        .integer('Secondary sequence must be an integer'),
    is_active: Yup.boolean()
});


const BrandViewModal = ({ brand, isOpen, onClose, onUpdate }) => {
    const dispatch = useDispatch();
    const { subBrands, subBrandsLoading, brands, loading, categories } = useSelector(state => state.admin);
    const [isSaving, setIsSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editedBrand, setEditedBrand] = useState(brand);
    const [validationErrors, setValidationErrors] = useState({});
    const [selectedSubBrand, setSelectedSubBrand] = useState(null);
    const [isSubBrandModalOpen, setIsSubBrandModalOpen] = useState(false);
    
    const currentBrand = brands.find(b => b.id === brand?.id) || brand;
    
    useEffect(() => {
        if (isOpen && currentBrand?.id) {
            dispatch(adminHandlers.fetchSubBrandsHandler(currentBrand.id));
            dispatch(adminHandlers.fetchCategoriesHandler());
            setEditedBrand({
                ...currentBrand,
                primary_category_id: currentBrand.primary_category?.id,
                secondary_category_id: currentBrand.secondary_category?.id
            });
            setIsEditMode(false);
            setValidationErrors({});
        }
    }, [isOpen, currentBrand?.id, dispatch]);

    const handleEditClick = () => {
        setIsEditMode(true);
        setValidationErrors({});
        setEditedBrand({
            ...currentBrand,
            primary_category_id: currentBrand.primary_category?.id,
            secondary_category_id: currentBrand.secondary_category?.id
        });
    };

    const handleCancelEdit = () => {
        setIsEditMode(false);
        setEditedBrand({
            ...currentBrand,
            primary_category_id: currentBrand.primary_category?.id,
            secondary_category_id: currentBrand.secondary_category?.id
        });
        setValidationErrors({});
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);

            const dataToSend = {
                name: editedBrand.name,
                primary_category_id: editedBrand.primary_category_id || null,
                secondary_category_id: editedBrand.secondary_category_id || null,
                primary_sequence: editedBrand.primary_sequence,
                secondary_sequence: editedBrand.secondary_sequence,
                is_active: editedBrand.is_active
            };

            // Only send image when it's a freshly picked base64 data URL (not the
            // existing S3 URL the backend already stored).
            if (editedBrand.image && editedBrand.image.startsWith("data:")) {
                dataToSend.image = editedBrand.image;
            }

            // Validate the form (validate editedBrand, not dataToSend)
            await brandValidationSchema.validate(editedBrand, { abortEarly: false });
                        
            // If validation passes, save the changes
            const updatedBrand = await dispatch(adminHandlers.updateBrandHandler(editedBrand.id, dataToSend));
            
            // Re-fetch the brands list to ensure Redux store is fully updated
            await dispatch(adminHandlers.fetchBrandsHandler({ page: 1, size: 100 }));
            
            // Update editedBrand with the fresh data from server
            if (updatedBrand) {
                setEditedBrand({
                    ...updatedBrand,
                    primary_category_id: updatedBrand.primary_category?.id,
                    secondary_category_id: updatedBrand.secondary_category?.id
                });
            }
            
            setIsEditMode(false);
            setValidationErrors({});
            setIsSaving(false);
            
            if (onUpdate && updatedBrand) {
                onUpdate(updatedBrand);
            }
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                // Format validation errors
                const errors = {};
                error.inner.forEach((err) => {
                    errors[err.path] = err.message;
                });
                setValidationErrors(errors);
            } else {
                console.error('Failed to update brand:', error);
            }
            setIsSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        let processedValue = value;
        if ((field === 'primary_category_id' || field === 'secondary_category_id') && value === '') {
            processedValue = null;
        }
        
        setEditedBrand(prev => ({
            ...prev,
            [field]: processedValue
        }));
        // Clear validation error for this field when user starts typing
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    // Don't render if no brand
    if (!brand) return null;

    return (
        <div className={`fixed inset-0 z-50 overflow-hidden ${isOpen ? '' : 'pointer-events-none'}`}>
            {/* Overlay */}
            <div 
                className={`absolute inset-0 bg-black transition-opacity duration-300 ease-in-out ${
                    isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
                }`}
                onClick={onClose}
            />
            
            {/* Sidebar Panel */}
            <div className={`fixed right-0 top-0 h-full w-[580px] bg-eerie-black shadow-xl flex flex-col transform transition-all duration-300 ease-in-out ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
            }`}>
                {/* Header */}
                <div className="flex flex-col">
                    <div className='border-b border-zinc-700'>
                        <button
                            onClick={onClose}
                            className="mx-4 my-3 bg-charleston-green rounded-lg transition-colors"
                            >
                            <DrawerIcon width={38} height={34} />
                        </button>
                    </div>

                    <div className="flex flex-col px-4 py-3">
                        <div className="flex justify-between items-center">
                            <h2 className="text-3xl text-white">Brand</h2>
                            {!isEditMode ? (
                                <div className="flex space-x-2">
                                    <button 
                                        onClick={handleEditClick}
                                        className="px-4 py-2 bg-charleston-green text-white border border-zinc-700 rounded-full hover:bg-zinc-800 flex items-center justify-center space-x-2"
                                        >
                                        <EditIcon width={20} height={20} />
                                        <span className="text-sm">Edit Brand</span>
                                    </button>
                                    <span></span>
                                </div>
                            ) : (
                                <div className="flex space-x-2">
                                    {!isSaving && (
                                        <button 
                                            onClick={handleCancelEdit}
                                            className="px-4 py-2 bg-charleston-green text-white border border-zinc-700 rounded-full hover:bg-zinc-800 flex" 
                                        >
                                            <span className="text-sm">Cancel</span>
                                        </button>
                                    )}
                                    <button 
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className={`px-4 py-2 text-white border rounded-full flex items-center justify-center space-x-2 ${
                                            isSaving 
                                                ? "bg-gray-600 border-gray-600 cursor-not-allowed" 
                                                : "bg-green-600 border-green-600 hover:bg-green-500"
                                        }`}
                                    >
                                        {isSaving ? (
                                            <>
                                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span className="text-sm">Saving...</span>
                                            </>
                                        ) : (
                                            <>
                                                <SaveIcon width={20} height={20} />
                                                <span className="text-sm">Save Changes</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditMode && Object.values(validationErrors).filter(Boolean).length > 0 && (
                            <div className="mt-2 p-3 bg-red-950 border border-red-800 rounded-lg">
                                <p className="text-red-400 text-sm font-medium mb-2">Please fix the following errors:</p>
                                <ul className="text-red-300 text-xs space-y-1">
                                    {Object.entries(validationErrors)
                                        .filter(([field, error]) => error) // Only show fields with actual error messages
                                        .map(([field, error]) => (
                                            <li key={field} className="flex items-start">
                                                <span className="text-red-500 mr-2">•</span>
                                                <span><strong className="capitalize">{field.replace(/_/g, ' ')}:</strong> {error}</span>
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        )}
                    </div>

                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="px-4 pb-4 pt-2 space-y-6">
                        {/* Brand Information Section */}
                        <div className="">
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="bg-charleston-green rounded-lg flex items-center justify-center">
                                    <BrandInfo width={32} height={32} />
                                </div>
                                <h3 className="text-white font-medium">Brand Information</h3>
                            </div>

                            <div className="space-y-4">
                                {/* Brand Logo */}
                                <div>
                                    <p className="text-md mb-2">Brand Logo</p>
                                    <div className="flex items-center space-x-4">
                                        <div className="w-20 h-20 bg-white p-0.5 inline-block rounded-lg">
                                            <img
                                                src={(isEditMode && editedBrand?.image) || currentBrand.image}
                                                alt={currentBrand.name}
                                                className="w-full h-full object-contain rounded-lg"
                                            />
                                        </div>
                                        {isEditMode && (
                                            <ImageUploadButton
                                                onPick={(dataUrl) => handleInputChange("image", dataUrl)}
                                                onReset={() => handleInputChange("image", currentBrand.image)}
                                                canReset={!!editedBrand?.image && editedBrand.image !== currentBrand.image}
                                                label="Change Logo"
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Product Name */}
                                <div>
                                    <p className="text-md mb-1">Product Name</p>
                                    <input
                                        type="text"
                                        value={isEditMode ? editedBrand?.name : currentBrand.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        readOnly={!isEditMode}
                                        className={`w-full bg-charleston-green border rounded-lg px-3 py-2 text-white text-sm ${
                                            validationErrors.name ? 'border-red-500' : 'border-zinc-700'
                                        } ${
                                            isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                        }`}
                                    />
                                    {validationErrors.name && (
                                        <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>
                                    )}
                                </div>

                                {/* Category */}
                                <div>
                                    <p className="text-md mb-1">Category</p>
                                    <div className="space-y-2">
                                        <div>
                                            <p className="text-xs text-zinc-500 mb-1">Primary Category</p>
                                            {!isEditMode ? (
                                                <input
                                                    type="text"
                                                    value={currentBrand.primary_category?.name || ''}
                                                    readOnly
                                                    className="w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                                                />
                                            ) : (
                                                <select
                                                    value={editedBrand?.primary_category_id || ''}
                                                    onChange={(e) => handleInputChange('primary_category_id', e.target.value)}
                                                    className="w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                                                >
                                                    <option value="">None</option>
                                                    {categories?.map(category => (
                                                        <option key={category.id} value={category.id}>
                                                            {category.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-xs text-zinc-500 mb-1">Secondary Category</p>
                                            {!isEditMode ? (
                                                <input
                                                    type="text"
                                                    value={currentBrand.secondary_category?.name || ''}
                                                    readOnly
                                                    className="w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
                                                />
                                            ) : (
                                                <select
                                                    value={editedBrand?.secondary_category_id || ''}
                                                    onChange={(e) => handleInputChange('secondary_category_id', e.target.value)}
                                                    className="w-full bg-charleston-green border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                                                >
                                                    <option value="">None</option>
                                                    {categories?.map(category => (
                                                        <option key={category.id} value={category.id}>
                                                            {category.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Additional Information Section */}
                        <div className="">
                            <div className="flex items-center space-x-2 mb-4">
                                <div className="bg-charleston-green rounded-lg flex items-center justify-center">
                                    <AdditionalInfoIcon width={32} height={32} />
                                </div>
                                <h3 className="text-white font-medium">Additional Information</h3>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <p className="text-md mb-1">Sequence</p>
                                    <div className="space-y-2">
                                        <div>
                                            <p className="text-xs text-zinc-500 mb-1">Primary Sequence</p>
                                            <input
                                                type="number"
                                                value={isEditMode ? editedBrand?.primary_sequence : currentBrand.primary_sequence}
                                                onChange={(e) => handleInputChange('primary_sequence', parseInt(e.target.value))}
                                                readOnly={!isEditMode}
                                                className={`w-full bg-charleston-green border rounded-lg px-3 py-2 text-white text-sm ${
                                                    validationErrors.primary_sequence ? 'border-red-500' : 'border-zinc-700'
                                                } ${
                                                    isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                                }`}
                                            />
                                            {validationErrors.primary_sequence && (
                                                <p className="text-red-500 text-xs mt-1">{validationErrors.primary_sequence}</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-xs text-zinc-500 mb-1">Secondary Sequence</p>
                                            <input
                                                type="number"
                                                value={isEditMode ? editedBrand?.secondary_sequence : currentBrand.secondary_sequence}
                                                onChange={(e) => handleInputChange('secondary_sequence', parseInt(e.target.value))}
                                                readOnly={!isEditMode}
                                                className={`w-full bg-charleston-green border rounded-lg px-3 py-2 text-white text-sm ${
                                                    validationErrors.secondary_sequence ? 'border-red-500' : 'border-zinc-700'
                                                } ${
                                                    isEditMode ? 'focus:border-blue-500 focus:outline-none' : ''
                                                }`}
                                            />
                                            {validationErrors.secondary_sequence && (
                                                <p className="text-red-500 text-xs mt-1">{validationErrors.secondary_sequence}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-md mb-2">Status</p>
                                    {!isEditMode ? (
                                        <span className={`inline-block px-4 py-1.5 text-xs rounded-full ${
                                            currentBrand.is_active
                                                ? 'bg-green-950 text-green-500 border border-green-500'
                                                : 'bg-red-950 text-red-500 border border-red-500'
                                        }`}>
                                            {currentBrand.is_active ? 'ACTIVE' : 'INACTIVE'}
                                        </span>
                                    ) : (
                                        <StatusToggle
                                            checked={!!editedBrand?.is_active}
                                            onChange={(v) => handleInputChange('is_active', v)}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Sub Brands Section */}
                        {subBrandsLoading ? (
                            <div className="text-center py-4">
                                <p className="text-zinc-400 text-sm">Loading sub brands...</p>
                            </div>
                        ) : subBrands && subBrands.length > 0 ? (
                            <div className="space-y-3">
                                <h3 className="text-white font-medium mb-2">Sub Brands</h3>
                                {subBrands.map((subBrand, index) => (
                                    <SubBrandCard 
                                        key={subBrand.id || index} 
                                        data={subBrand} 
                                        onEdit={(subBrand) => {
                                            setSelectedSubBrand(subBrand);
                                            // Delay opening to allow modal to mount first
                                            setTimeout(() => {
                                                setIsSubBrandModalOpen(true);
                                            }, 10);
                                        }}
                                    />
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* SubBrand Modal */}
            {selectedSubBrand && (
                <SubBrandModal
                    subBrand={selectedSubBrand}
                    isOpen={isSubBrandModalOpen}
                    onClose={() => {
                        setIsSubBrandModalOpen(false);
                        // Delay clearing selectedSubBrand to allow close animation
                        setTimeout(() => {
                            setSelectedSubBrand(null);
                        }, 300);
                    }}
                    onUpdate={(updatedSubBrand) => {
                        // Update the selected sub-brand with the new data
                        if (updatedSubBrand) {
                            setSelectedSubBrand(updatedSubBrand);
                        }
                        // Refresh sub-brands after update
                        dispatch(adminHandlers.fetchSubBrandsHandler(currentBrand.id));
                    }}
                    brandId={currentBrand?.id}
                />
            )}
        </div>
    );
};

export default BrandViewModal;